import React, { useRef, useState } from 'react';
import { Button, Spinner } from 'reactstrap';

import { ASSET_ACCEPT, isVideo, MAX_ASSETS, posterSrc } from './advertModel';

const TILE = 96;

/** One gallery tile: the screenshot, or the video's first frame with a VIDEO tag; hover for move/remove. */
const AssetTile = ({ asset, index, count, disabled, onMove, onRemove }) => {
  const [hover, setHover] = useState(false);
  const controls = hover && !disabled;
  return (
    <div
      className="position-relative border rounded overflow-hidden flex-shrink-0 bg-light"
      style={{ width: TILE, height: TILE }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {isVideo(asset) ? (
        // muted + metadata only: the tile shows the first frame, nothing plays or downloads on the form
        <video src={posterSrc(asset.url)} muted preload="metadata" style={{ width: TILE, height: TILE, objectFit: 'cover' }} />
      ) : (
        <img src={asset.url} alt="" style={{ width: TILE, height: TILE, objectFit: 'cover' }} />
      )}
      {isVideo(asset) ? (
        <span
          className="position-absolute bottom-0 start-0 m-1 px-1 rounded text-white"
          style={{ fontSize: 10, backgroundColor: 'rgba(0,0,0,.65)' }}
        >
          VIDEO
        </span>
      ) : null}
      {controls ? (
        <div
          className="position-absolute top-0 start-0 d-flex align-items-center justify-content-center"
          style={{ width: TILE, height: TILE, backgroundColor: 'rgba(0,0,0,.45)' }}
        >
          <Button color="light" size="sm" className="px-1 py-0 me-1" title="Move left" disabled={index === 0} onClick={() => onMove(index, index - 1)}>
            ‹
          </Button>
          <Button color="light" size="sm" className="px-1 py-0 me-1" title="Move right" disabled={index === count - 1} onClick={() => onMove(index, index + 1)}>
            ›
          </Button>
          <Button color="danger" size="sm" className="px-1 py-0" title="Remove" onClick={() => onRemove(index)}>
            ×
          </Button>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Screenshots & preview (UN-3334): the advert's gallery as a row of tiles in display order, plus an Add tile
 * that opens the file picker (images and video, several at once). This is the "assets" upload the old
 * single image field grew into once the icon moved onto the preview tile; the icon stays separate.
 * Rules the server also enforces — at most MAX_ASSETS, one video — are applied by the form's handler.
 */
const AdvertAssets = ({ assets = [], disabled = false, uploading = false, onAdd, onMove, onRemove }) => {
  const input = useRef(null);
  const full = assets.length >= MAX_ASSETS;
  const canAdd = !disabled && !uploading && !full;
  return (
    <div className="d-flex flex-wrap gap-2">
      {assets.map((asset, index) => (
        <AssetTile
          // the url is unique per upload; keying on the index would remount every tile a move shifts
          key={asset.url}
          asset={asset}
          index={index}
          count={assets.length}
          disabled={disabled || uploading}
          onMove={onMove}
          onRemove={onRemove}
        />
      ))}
      <div
        role="button"
        tabIndex={canAdd ? 0 : -1}
        aria-label="Add screenshots or a preview video"
        aria-disabled={!canAdd}
        className="d-flex flex-column align-items-center justify-content-center rounded text-muted flex-shrink-0"
        style={{
          width: TILE,
          height: TILE,
          border: '2px dashed #ced4da',
          cursor: canAdd ? 'pointer' : 'default',
          opacity: canAdd ? 1 : 0.5
        }}
        onClick={() => canAdd && input.current && input.current.click()}
        onKeyDown={e => {
          if (canAdd && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            input.current.click();
          }
        }}
      >
        {uploading ? (
          <Spinner size="sm" />
        ) : (
          <>
            <span style={{ fontSize: 24, lineHeight: 1 }}>+</span>
            <span style={{ fontSize: 11 }}>{full ? 'Full' : 'Add'}</span>
          </>
        )}
        <input ref={input} type="file" accept={ASSET_ACCEPT} multiple className="d-none" onChange={onAdd} />
      </div>
    </div>
  );
};

export default AdvertAssets;
